import React from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight, Star } from "lucide-react";
import { buildWhatsAppLink } from "../lib/constants";
import FooterCTA from "../components/FooterCTA";
import useDocumentMeta from "../lib/useDocumentMeta";
import { useCollection } from "../lib/useCollection";
import { resolveMediaUrl } from "../lib/mediaUrl";

const FALLBACK_PACKAGES = [
    { id: "starter", slug: "starter", name: "Starter Pack", price_label: "₹4,999", price_sub: "one-time", category: "branding", tagline: "Just need a great logo to start.", features: ["Custom logo design", "2 revision rounds", "PNG & PDF deliverables", "Light & dark variants", "Basic usage guidance"], highlight: false, badge: "" },
    { id: "business", slug: "business", name: "Business Pack", price_label: "₹9,999", price_sub: "one-time", category: "branding", tagline: "Logo + identity essentials to launch.", features: ["Logo design (Starter included)", "Visiting card design", "Letterhead design", "Social media starter kit", "Source files included", "3 revision rounds"], highlight: true, badge: "Most Popular" },
    { id: "premium", slug: "premium", name: "Premium Pack", price_label: "₹19,999", price_sub: "one-time", category: "branding", tagline: "Full brand system, signage and packaging.", features: ["Full brand identity system", "Packaging design (1 SKU)", "Signage design (concept + artwork)", "Brand guidelines PDF", "Social media template pack", "5 revision rounds"], highlight: false, badge: "" },
    { id: "enterprise", slug: "enterprise", name: "Enterprise Pack", price_label: "Custom", price_sub: "talk to us", category: "branding", tagline: "Everything + website + ongoing support.", features: ["Everything in Premium", "Website design & build", "Multi-SKU packaging", "On-site signage installation", "Dedicated brand manager", "Ongoing monthly retainer"], highlight: false, badge: "" },
];

const buildPackageLink = (pkg) =>
    buildWhatsAppLink(
        `Hi Raj Graphics! I'd like to know more about the ${pkg.name} (${pkg.price_label}). Please share details and next steps.`,
    );

const PackageCard = ({ pkg, i, showImage = false }) => (
    <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.15 }}
        transition={{ duration: 0.55, delay: i * 0.07 }}
        data-testid={`package-card-${pkg.slug || pkg.id}`}
        className={`relative flex flex-col rounded-2xl overflow-hidden transition-colors ${
            pkg.highlight
                ? "bg-gradient-to-b from-gold/[0.10] to-gold/[0.02] border border-gold/40"
                : "bg-white/[0.02] border border-white/5 hover:border-gold/30"
        }`}
    >
        {showImage && pkg.image_url && (
            <div className="relative h-44 md:h-52 overflow-hidden">
                <img
                    src={resolveMediaUrl(pkg.image_url)}
                    alt={`${pkg.name} — website design reference by Raj Graphics`}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover"
                    data-testid={`package-image-${pkg.slug || pkg.id}`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
            </div>
        )}

        <div className="flex flex-col flex-1 p-7 md:p-8">
            {pkg.badge && (
                <div className={`absolute ${showImage && pkg.image_url ? "top-4" : "-top-0"} left-7 z-10 inline-flex items-center gap-1.5 bg-gold text-black text-[10px] uppercase tracking-[0.25em] font-medium font-inter px-3 py-1.5 rounded-full mt-3`}>
                    <Star size={11} fill="currentColor" strokeWidth={0} />
                    {pkg.badge}
                </div>
            )}

            <h3 className="font-poppins font-medium text-white text-xl">{pkg.name}</h3>
            <p className="mt-2 font-inter text-sm text-zinc-400">{pkg.tagline}</p>

            <div className="mt-6 flex items-baseline gap-2">
                <div
                    className={`font-poppins font-semibold text-3xl md:text-4xl ${
                        pkg.highlight ? "text-gold" : "text-white"
                    }`}
                    data-testid={`package-price-${pkg.slug || pkg.id}`}
                >
                    {pkg.price_label}
                </div>
                <div className="font-inter text-xs uppercase tracking-[0.2em] text-zinc-500">
                    {pkg.price_sub}
                </div>
            </div>

            <ul className="mt-7 space-y-3 flex-1">
                {(pkg.features || []).map((f) => (
                    <li key={f} className="flex items-start gap-3">
                        <span
                            className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 border ${
                                pkg.highlight ? "bg-gold/20 border-gold/40" : "bg-white/5 border-white/10"
                            }`}
                        >
                            <Check size={11} className="text-gold" strokeWidth={2.6} />
                        </span>
                        <span className="font-inter text-sm text-zinc-300 leading-relaxed">{f}</span>
                    </li>
                ))}
            </ul>

            <a
                href={buildPackageLink(pkg)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`package-cta-${pkg.slug || pkg.id}`}
                className={`mt-8 group inline-flex items-center justify-center gap-2 font-medium font-inter px-6 py-3 rounded-full transition-all ${
                    pkg.highlight
                        ? "bg-gold text-black hover:bg-[#C5A030] hover:shadow-[0_10px_40px_rgba(212,175,55,0.35)]"
                        : "border border-white/15 text-white hover:border-gold hover:text-gold"
                }`}
            >
                Get This Package
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </a>
        </div>
    </motion.div>
);

const ShopPage = () => {
    useDocumentMeta({
        title: "Design & Website Packages with Prices | Raj Graphics Nagpur",
        description:
            "Transparent packages from Raj Graphics Nagpur — logo, brand identity, signage and packaging packs, plus website design packages for small business, corporate and eCommerce.",
        canonical: "/shop",
    });

    const packages = useCollection("/packages", FALLBACK_PACKAGES);
    const branding = packages.filter((p) => p.category !== "web");
    const web = packages.filter((p) => p.category === "web");

    return (
        <main data-testid="shop-page" className="bg-[#050505]">
            {/* Hero */}
            <section className="relative pt-36 pb-12 md:pt-44 md:pb-16 overflow-hidden">
                <div className="absolute inset-0 grid-pattern radial-fade opacity-60 pointer-events-none" />
                <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-gold/[0.07] blur-[140px] pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-6 md:px-12">
                    <div className="font-inter text-xs uppercase tracking-[0.25em] text-gold mb-5">
                        Shop · Design Packages
                    </div>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        className="font-poppins font-bold tracking-tight text-white text-4xl sm:text-5xl lg:text-7xl leading-[1.05] max-w-4xl"
                        data-testid="shop-headline"
                    >
                        Pick the pack. <span className="gold-gradient-text">Skip the back‑and‑forth.</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="mt-6 font-inter text-base sm:text-lg text-zinc-400 max-w-2xl leading-relaxed"
                    >
                        Transparent, all‑inclusive packages built for growing businesses. Pick a
                        starting point — we'll tailor scope on the call.
                    </motion.p>
                </div>
            </section>

            {/* Branding packages */}
            <section className="relative py-12 md:py-16">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="mb-8">
                        <h2 className="font-poppins font-semibold text-white text-2xl md:text-3xl tracking-tight" data-testid="branding-packages-heading">
                            Branding & Design Packages
                        </h2>
                        <p className="mt-2 font-inter text-sm text-zinc-400">
                            Logo, identity, signage and packaging — everything a brand needs to launch.
                        </p>
                    </div>
                    <div
                        data-testid="packages-grid"
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6"
                    >
                        {branding.map((pkg, i) => (
                            <PackageCard key={pkg.id || pkg.slug} pkg={pkg} i={i} />
                        ))}
                    </div>
                </div>
            </section>

            {/* Web development packages */}
            {web.length > 0 && (
                <section className="relative py-12 md:py-16 border-t border-white/5" data-testid="web-packages-section">
                    <div className="max-w-7xl mx-auto px-6 md:px-12">
                        <div className="mb-8">
                            <div className="font-inter text-xs uppercase tracking-[0.25em] text-gold mb-3">
                                New
                            </div>
                            <h2 className="font-poppins font-semibold text-white text-2xl md:text-3xl tracking-tight" data-testid="web-packages-heading">
                                Website Design Packages
                            </h2>
                            <p className="mt-2 font-inter text-sm text-zinc-400 max-w-2xl">
                                Fast, mobile-friendly websites that match your brand — from a simple
                                business site to a full online store.
                            </p>
                        </div>
                        <div
                            data-testid="web-packages-grid"
                            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6"
                        >
                            {web.map((pkg, i) => (
                                <PackageCard key={pkg.id || pkg.slug} pkg={pkg} i={i} showImage />
                            ))}
                        </div>
                    </div>
                </section>
            )}

            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <p className="py-8 font-inter text-sm text-zinc-500 text-center">
                    Prices are indicative. Final scope &amp; delivery timeline are confirmed on the strategy call.
                </p>
            </div>

            <FooterCTA />
        </main>
    );
};

export default ShopPage;
